import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@apple/utils/pagination/models';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { marketQueries } from '@apple/features/market/queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/markets')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerGroupManagement.Read',
		});

		return {
			getMarketsQueryOptions: marketQueries.getMarkets(),
		};
	},
	loader: ({ context: { queryClient, getMarketsQueryOptions } }) => {
		void queryClient.prefetchQuery(getMarketsQueryOptions);
	},
	component: MarketManagementRoute,
});

function MarketManagementRoute() {
	const { t } = useTranslation('market');
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const { getMarketsQueryOptions } = Route.useRouteContext();
	const marketsQuery = useQuery(getMarketsQueryOptions);

	const table = useTable({
		features,
		data: marketsQuery.data,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: false,
		columnResizeMode: 'onChange',
		initialState: {
			pagination: {
				pageSize: DEFAULT_PAGE_SIZE,
				pageIndex: DEFAULT_PAGE_INDEX,
			},
			sorting: [
				{
					id: 'name',
					desc: true,
				},
			],
		},
		columns: [
			{
				accessorKey: 'name',
				header: t('labels.name'),
				filter: {
					group: t('filterGroups.details'),
				},
				size: 250,
				cell: ({ row }) => (
					<Link
						to='/manage/markets/$marketId'
						state={{ from: location }}
						params={{ marketId: row.original.id }}
						title={row.original.name}
						children={row.original.name}
					/>
				),
			},
			{
				accessorKey: 'code',
				header: t('labels.marketCode'),
				filter: {
					group: t('filterGroups.details'),
				},
			},
		],
	});

	return (
		<TableLayout
			title={t('title.list')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('buttons.add')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/manage/markets/add',
							state: { from: location },
						})
					}
				/>,
			]}
		>
			<DataTable table={table} loading={marketsQuery.isFetching} />
		</TableLayout>
	);
}
